// MusicXML key analysis and transposition.
// Pitches are moved along the line of fifths, so spelling follows the new key
// (Gb major up a semitone gives G major with F#, not Ab-flavoured enharmonics).

import {isMidi} from "./midi";

export type Mode = "major" | "minor";

export interface ScoreAnalysis {
    fifths: number;
    mode: Mode;
    // How many notes fall on each pitch class, C = 0
    pitchClassCounts: number[];
    noteCount: number;
    minMidi: number;
    maxMidi: number;
}

export interface KeyOption {
    fifths: number;
    fifthsDelta: number;
    // Semitones up, 0..11; the actual shift may go down by 12 - pcShift instead
    pcShift: number;
    blackCount: number;
    blackRatio: number;
}

const STEP_PC: Record<string, number> = {C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11};
// Line of fifths starting at F = -1
const LOF_STEPS = ["F", "C", "G", "D", "A", "E", "B"];
const SHARP_ORDER = ["F", "C", "G", "D", "A", "E", "B"];
const FLAT_ORDER = ["B", "E", "A", "D", "G", "C", "F"];
const BLACK_PCS = new Set([1, 3, 6, 8, 10]);
const ACCIDENTAL_NAMES: Record<number, string> = {
    [-2]: "flat-flat", [-1]: "flat", 0: "natural", 1: "sharp", 2: "double-sharp",
};
// Children of <note> that come before <accidental> in the MusicXML schema
const BEFORE_ACCIDENTAL = new Set([
    "grace", "cue", "chord", "pitch", "unpitched", "rest", "duration", "tie",
    "instrument", "footnote", "level", "voice", "type", "dot",
]);

const mod = (n: number, m: number) => ((n % m) + m) % m;

export const isBlackKey = (pc: number) => BLACK_PCS.has(mod(pc, 12));

function stepToLof(step: string, alter: number): number {
    return LOF_STEPS.indexOf(step) - 1 + 7 * alter;
}

function lofToStep(lof: number): { step: string; alter: number } {
    // Avoid triple sharps/flats by respelling a diminished second away
    while (Math.floor((lof + 1) / 7) > 2) lof -= 12;
    while (Math.floor((lof + 1) / 7) < -2) lof += 12;
    const i = lof + 1;
    return {step: LOF_STEPS[mod(i, 7)], alter: Math.floor(i / 7)};
}

// Spelling for a bare pitch (MIDI): notes of the key as written, others from a window
// one fifth to the flat side and four to the sharp side (in C: B♭, F♯, C♯, G♯, D♯)
export function spellMidi(midi: number, fifths: number): { step: string; alter: number; octave: number } {
    const pc = mod(midi, 12);
    let lof = fifths - 2;
    while (mod(7 * lof, 12) !== pc) lof++;
    const {step, alter} = lofToStep(lof);
    return {step, alter, octave: (midi - STEP_PC[step] - alter) / 12 - 1};
}

function keyAlter(fifths: number, step: string): number {
    if (fifths > 0 && SHARP_ORDER.indexOf(step) < fifths) return 1;
    if (fifths < 0 && FLAT_ORDER.indexOf(step) < -fifths) return -1;
    return 0;
}

function children(el: Element, name: string): Element[] {
    return Array.from(el.children).filter(c => c.tagName === name);
}

function child(el: Element, name: string): Element | undefined {
    return children(el, name)[0];
}

function childText(el: Element, name: string): string | undefined {
    return child(el, name)?.textContent?.trim();
}

function childNumber(el: Element, name: string, fallback = 0): number {
    const value = parseFloat(childText(el, name) ?? "");
    return Number.isFinite(value) ? value : fallback;
}

function setChildText(el: Element, name: string, value: string, before?: string) {
    let c = child(el, name);
    if (!c) {
        c = el.ownerDocument.createElement(name);
        el.insertBefore(c, (before && child(el, before)) || null);
    }
    c.textContent = value;
}

function removeChild(el: Element, name: string) {
    children(el, name).forEach(c => el.removeChild(c));
}

function midiOf(step: string, alter: number, octave: number) {
    return 12 * (octave + 1) + STEP_PC[step] + alter;
}

// A note that only continues a tie is not struck again
function isTieContinuation(note: Element): boolean {
    const types = children(note, "tie").map(t => t.getAttribute("type"));
    return types.includes("stop") && !types.includes("start");
}

export function parseScore(xml: string): Document {
    const doc = new DOMParser().parseFromString(xml, "application/xml");
    if (doc.getElementsByTagName("parsererror").length > 0) {
        throw new Error("Файл не похож на MusicXML: не удалось разобрать XML.");
    }
    const root = doc.documentElement.tagName;
    if (root === "score-timewise") {
        throw new Error("Формат score-timewise не поддерживается. Пересохраните файл как обычный MusicXML.");
    }
    if (root !== "score-partwise") {
        throw new Error("Файл не похож на MusicXML: нет элемента score-partwise.");
    }
    return doc;
}

export function serializeScore(doc: Document): string {
    const body = new XMLSerializer().serializeToString(doc);
    return body.startsWith("<?xml") ? body : `<?xml version="1.0" encoding="UTF-8"?>\n${body}`;
}

export function analyzeScore(doc: Document): ScoreAnalysis {
    const firstKey = Array.from(doc.getElementsByTagName("key")).find(k => child(k, "fifths"));
    const counts = new Array(12).fill(0);
    let noteCount = 0;
    let minMidi = Infinity;
    let maxMidi = -Infinity;

    for (const note of Array.from(doc.getElementsByTagName("note"))) {
        const pitch = child(note, "pitch");
        if (!pitch || isTieContinuation(note)) continue;
        const step = childText(pitch, "step") ?? "C";
        const midi = midiOf(step, Math.round(childNumber(pitch, "alter")), childNumber(pitch, "octave", 4));
        counts[mod(midi, 12)]++;
        noteCount++;
        minMidi = Math.min(minMidi, midi);
        maxMidi = Math.max(maxMidi, midi);
    }

    return {
        fifths: firstKey ? childNumber(firstKey, "fifths") : 0,
        mode: firstKey && childText(firstKey, "mode") === "minor" ? "minor" : "major",
        pitchClassCounts: counts,
        noteCount,
        minMidi,
        maxMidi,
    };
}

// Pitch classes of a plain scale, for when there is only a key and no file
export function scalePitchClasses(fifths: number, mode: Mode): number[] {
    const tonic = lofToStep(mode === "minor" ? fifths + 3 : fifths);
    const tonicPc = STEP_PC[tonic.step] + tonic.alter;
    const degrees = mode === "minor" ? [0, 2, 3, 5, 7, 8, 10] : [0, 2, 4, 5, 7, 9, 11];
    const counts = new Array(12).fill(0);
    degrees.forEach(d => counts[mod(tonicPc + d, 12)]++);
    return counts;
}

// One option per pitch class, spelled with at most six sharps or flats
export function keyOptions(fromFifths: number, pitchClassCounts: number[]): KeyOption[] {
    const total = pitchClassCounts.reduce((a, b) => a + b, 0);
    const targets = [-6, -5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5].map(f => (f === -6 && fromFifths === 6 ? 6 : f));
    return targets.map(fifths => {
        const fifthsDelta = fifths - fromFifths;
        const pcShift = mod(7 * fifthsDelta, 12);
        const blackCount = pitchClassCounts.reduce((sum, n, pc) => sum + (isBlackKey(pc + pcShift) ? n : 0), 0);
        return {fifths, fifthsDelta, pcShift, blackCount, blackRatio: total ? blackCount / total : 0};
    });
}

// Fewest black keys first, then the smallest move, then the simplest signature
export function rankOptions(options: KeyOption[]): KeyOption[] {
    return [...options].sort((a, b) =>
        a.blackCount - b.blackCount
        || Math.min(a.pcShift, 12 - a.pcShift) - Math.min(b.pcShift, 12 - b.pcShift)
        || Math.abs(a.fifths) - Math.abs(b.fifths));
}

export function nearestShift(pcShift: number): number {
    return pcShift <= 5 ? pcShift : pcShift - 12;
}

function transposeStepPair(el: Element, stepName: string, alterName: string, delta: number) {
    const step = childText(el, stepName);
    if (!step || !(step in STEP_PC)) return;
    const moved = lofToStep(stepToLof(step, Math.round(childNumber(el, alterName))) + delta);
    setChildText(el, stepName, moved.step);
    if (moved.alter) {
        const next = child(el, stepName)?.nextElementSibling;
        if (!child(el, alterName)) el.insertBefore(el.ownerDocument.createElement(alterName), next ?? null);
        setChildText(el, alterName, String(moved.alter));
    } else {
        removeChild(el, alterName);
    }
}

function setAccidental(note: Element, alter: number | null) {
    removeChild(note, "accidental");
    if (alter === null) return;
    const acc = note.ownerDocument.createElement("accidental");
    acc.textContent = ACCIDENTAL_NAMES[alter] ?? "natural";
    const anchor = Array.from(note.children).filter(c => BEFORE_ACCIDENTAL.has(c.tagName)).pop();
    note.insertBefore(acc, anchor ? anchor.nextSibling : note.firstChild);
}

interface PlacedNote {
    note: Element;
    onset: number;
    staff: number;
    keyFifths: number;
    step: string;
    alter: number;
    octave: number;
}

// Writes accidentals that the new key signature requires, measure by measure,
// in time order so that several voices on one staff share the same state
function respellAccidentals(placed: PlacedNote[]) {
    const byStaff = new Map<number, PlacedNote[]>();
    placed.forEach(p => byStaff.set(p.staff, [...(byStaff.get(p.staff) ?? []), p]));
    byStaff.forEach(notes => {
        const state = new Map<string, number>();
        notes
            .map((p, i) => ({p, i}))
            .sort((a, b) => a.p.onset - b.p.onset || a.i - b.i)
            .forEach(({p}) => {
                if (isTieContinuation(p.note)) {
                    setAccidental(p.note, null);
                    return;
                }
                const id = `${p.step}${p.octave}`;
                const expected = state.has(id) ? state.get(id)! : keyAlter(p.keyFifths, p.step);
                if (p.alter !== expected) {
                    setAccidental(p.note, p.alter);
                    state.set(id, p.alter);
                } else {
                    setAccidental(p.note, null);
                }
            });
    });
}

/**
 * Moves every pitch, key signature and chord symbol by fifthsDelta steps on the
 * line of fifths and by semitones in pitch. The two must agree modulo 12
 * (semitones ≡ 7 · fifthsDelta), which keyOptions guarantees.
 */
export function transposeScore(source: Document, fifthsDelta: number, semitones: number): Document {
    const doc = source.cloneNode(true) as Document;

    for (const part of children(doc.documentElement, "part")) {
        // Later key changes may land on 7+ accidentals; those get respelled by ±12 fifths
        let delta = fifthsDelta;
        let defaultKey = 0;
        const staffKeys = new Map<number, number>();

        for (const measure of children(part, "measure")) {
            const placed: PlacedNote[] = [];
            let cursor = 0;
            let lastOnset = 0;

            for (const el of Array.from(measure.children)) {
                switch (el.tagName) {
                    case "attributes":
                        for (const key of children(el, "key")) {
                            if (!child(key, "fifths")) continue;
                            let fifths = childNumber(key, "fifths") + fifthsDelta;
                            const shift = fifths > 6 ? -12 : fifths < -6 ? 12 : 0;
                            fifths += shift;
                            delta = fifthsDelta + shift;
                            setChildText(key, "fifths", String(fifths));
                            const number = key.getAttribute("number");
                            if (number) {
                                staffKeys.set(Number(number), fifths);
                            } else {
                                defaultKey = fifths;
                                staffKeys.clear();
                            }
                        }
                        break;
                    case "harmony":
                        for (const root of children(el, "root")) transposeStepPair(root, "root-step", "root-alter", delta);
                        for (const bass of children(el, "bass")) transposeStepPair(bass, "bass-step", "bass-alter", delta);
                        break;
                    case "backup":
                        cursor -= childNumber(el, "duration");
                        break;
                    case "forward":
                        cursor += childNumber(el, "duration");
                        break;
                    case "note": {
                        let onset = lastOnset;
                        if (!child(el, "chord")) {
                            onset = lastOnset = cursor;
                            if (!child(el, "grace")) cursor += childNumber(el, "duration");
                        }
                        const pitch = child(el, "pitch");
                        if (!pitch) break;

                        const step = childText(pitch, "step") ?? "C";
                        const rawAlter = childNumber(pitch, "alter");
                        const alter = Math.round(rawAlter);
                        const microtone = rawAlter - alter;
                        const midi = midiOf(step, alter, childNumber(pitch, "octave", 4)) + semitones;
                        const moved = lofToStep(stepToLof(step, alter) + delta);
                        const octave = (midi - STEP_PC[moved.step] - moved.alter) / 12 - 1;

                        setChildText(pitch, "step", moved.step);
                        if (moved.alter || microtone) {
                            setChildText(pitch, "alter", String(moved.alter + microtone), "octave");
                        } else {
                            removeChild(pitch, "alter");
                        }
                        setChildText(pitch, "octave", String(octave));

                        const staff = childNumber(el, "staff", 1);
                        placed.push({
                            note: el, onset, staff,
                            keyFifths: staffKeys.get(staff) ?? defaultKey,
                            step: moved.step, alter: moved.alter, octave,
                        });
                        break;
                    }
                }
            }
            respellAccidentals(placed);
        }
    }
    // Stem directions follow pitch, so after a real move the engraver should choose them again
    if (semitones !== 0) Array.from(doc.getElementsByTagName("stem")).forEach(st => st.parentElement?.removeChild(st));
    return doc;
}

function decodeText(bytes: Uint8Array): string {
    if (bytes[0] === 0xff && bytes[1] === 0xfe) return new TextDecoder("utf-16le").decode(bytes);
    if (bytes[0] === 0xfe && bytes[1] === 0xff) return new TextDecoder("utf-16be").decode(bytes);
    return new TextDecoder("utf-8").decode(bytes);
}

export class PdfNotSupportedError extends Error {
}

export type MusicFile = { kind: "xml"; text: string } | { kind: "midi"; bytes: Uint8Array };

// Accepts .musicxml/.xml, compressed .mxl and MIDI; the format is told by content, not extension
export async function readMusicFile(file: File): Promise<MusicFile> {
    const bytes = new Uint8Array(await file.arrayBuffer());
    if (bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46) {
        throw new PdfNotSupportedError("PDF");
    }
    if (isMidi(bytes)) return {kind: "midi", bytes};
    if (bytes[0] !== 0x50 || bytes[1] !== 0x4b) return {kind: "xml", text: decodeText(bytes)};

    const {default: JSZip} = await import("jszip");
    const zip = await JSZip.loadAsync(bytes);
    let path: string | undefined;
    const container = zip.file("META-INF/container.xml");
    if (container) {
        const manifest = new DOMParser().parseFromString(await container.async("string"), "application/xml");
        path = manifest.getElementsByTagName("rootfile")[0]?.getAttribute("full-path") ?? undefined;
    }
    path ??= Object.keys(zip.files).find(p => !p.startsWith("META-INF") && /\.(xml|musicxml)$/i.test(p));
    const entry = path ? zip.file(path) : null;
    if (!entry) throw new Error("В архиве .mxl не нашлось нотного файла.");
    return {kind: "xml", text: decodeText(await entry.async("uint8array"))};
}

const SYLLABLES: Record<string, string> = {C: "до", D: "ре", E: "ми", F: "фа", G: "соль", A: "ля", B: "си"};

export function keyName(fifths: number, mode: Mode): { ru: string; letter: string } {
    const tonic = lofToStep(mode === "minor" ? fifths + 3 : fifths);
    const sign = tonic.alter === -1 ? "-бемоль" : tonic.alter === 1 ? "-диез" : "";
    const letterSign = tonic.alter === -1 ? "♭" : tonic.alter === 1 ? "♯" : "";
    return {
        ru: `${SYLLABLES[tonic.step]}${sign} ${mode === "minor" ? "минор" : "мажор"}`,
        letter: `${tonic.step}${letterSign}${mode === "minor" ? "m" : ""}`,
    };
}

export function signatureLabel(fifths: number): string {
    if (fifths === 0) return "без знаков";
    return `${Math.abs(fifths)} ${fifths > 0 ? "♯" : "♭"}`;
}

export function semitoneLabel(semitones: number): string {
    if (semitones === 0) return "без сдвига";
    const n = Math.abs(semitones);
    const word = n === 1 ? "полутон" : n < 5 ? "полутона" : "полутонов";
    return `${semitones > 0 ? "↑" : "↓"} ${n} ${word}`;
}
